// ====================
// Return Basket Module
// ====================

import { orderBy } from 'lodash-es';
import qs          from 'qs';
import axios       from '@/axios';
import ReturnBasketLine  from '@/models/ReturnBasketLine';

export function setupReturnBasketModule(i18n)
{
    return {
        namespaced: true,

        state()
        {
            return {
                lines:       [],
                lastReturns: [],
            };
        },

        getters:
        {
            getLinesByArticleCode: state => articleCode => state.lines.filter(l => l.article_code === articleCode),

            getLineByPairId: state => lensPairId =>
            {
                // Build other side lens pair ID.
                const letter = lensPairId.endsWith('A') ? 'B' : 'A';
                const otherSideLensPairId = lensPairId.slice(0, -1) + letter;

                // Look for other side lens.
                const line = state.lines.find(line => line.article_data?.LENS_PAIR_ID == otherSideLensPairId);
                if(!line)
                {
                    return null;
                }

                return new BasketLine(line);
            },

            getById: state => id =>
            {
                const line = state.lines.find(line => line.id == id);

                if(!line)
                {
                    return null;
                }

                return new ReturnBasketLine(line);
            },
        },

        mutations:
        {
            /**
             *
             * @param {Object} state
             */
            setLines(state, lines)
            {
                state.lines = orderBy(lines, ['created_at'], ['desc']);
            },

            reset(state, lineIds = [])
            {
                if(lineIds.length)
                {
                    const lines = [];

                    for(const i in state.lines)
                    {
                        if(!lineIds.includes(state.lines[i].id))
                        {
                            lines.push(state.lines[i]);
                        }
                    }

                    state.lines = lines;
                }
                else
                {
                    state.lines = [];
                }
            },

            setLastReturns(state, returns)
            {
                state.lastReturns = returns;
            },

            setLastOptions(state, options)
            {
                state.lastOptions = options;
            },
        },

        actions:
        {
            /**
             * Load return basket lines from the server.
             *
             * @param {Object} param
             */
            load({ commit, rootState })
            {
                return new Promise((resolve, reject) =>
                {
                    const cEntityId = rootState.account.cEntity.id;

                    axios.get(`/api/return-basket/${cEntityId}`)
                        .then(response =>
                        {
                            commit('setLines', response.data);
                            resolve(response.data);
                        })
                        .catch(error =>
                        {
                            console.log(error);
                            reject(error);
                        });
                });
            },

            /**
             * Save one or multiple lines to the basket.
             *
             * @param {Object} commit
             */
            saveLines({ commit, dispatch, rootState }, lines)
            {
                return new Promise((resolve, reject) =>
                {
                    const cEntityId = rootState.account.cEntity.id;

                    if(!Array.isArray(lines))
                    {
                        lines = [lines];
                    }

                    axios.post(`/api/return-basket/${cEntityId}`, { lines })
                        .then(response =>
                        {
                            commit('setLines', response.data);

                            resolve(response.data);
                        })
                        .catch(error =>
                        {
                            console.log(error);
                            reject(error);
                        });
                });

            },

            /**
             * Delete a line from the basket.
             *
             * @param {Object} commit
             */
            deleteLine({ commit, dispatch, rootState }, lineId)
            {
                return new Promise((resolve, reject) =>
                {
                    return axios.delete(`/api/return-basket/${rootState.account.cEntity.id}/${lineId}`)
                        .then(response =>
                        {
                            commit('setLines', response.data);

                            resolve(response.data);
                        })
                        .catch(error =>
                        {
                            console.log(error);

                            reject(error);
                        });
                });
            },

            /**
             * Empty the basket.
             *
             * @param {Object} commit
             */
            deleteAllLines({ commit, dispatch, rootState })
            {
                return new Promise((resolve, reject) =>
                {
                    return axios.delete(`/api/return-basket/${rootState.account.cEntity.id}`)
                        .then(response =>
                        {
                            commit('reset');

                            resolve(response.data);
                        })
                        .catch(error =>
                        {
                            console.log(error);
                            reject(error);
                        });
                });
            },

            /**
             * Make the return(s).
             */
            make({ commit, dispatch, rootState }, { options, selectedReturnBasketLineIds })
            {
                return new Promise((resolve, reject) =>
                {
                    let url = ('/api/return/:eid').replace(':eid', rootState.account.cEntity.id);
                    const data = { options };

                    if(selectedReturnBasketLineIds)
                    {
                        url += '?' + qs.stringify({ return_basket_line_ids: selectedReturnBasketLineIds });
                    }

                    commit('overlay/setText', i18n.global.t('overlay.placing'), { root: true });
                    commit('overlay/setVisibility', true, { root: true });

                    axios.post(url, data)
                        .then(response =>
                        {
                            commit('reset', selectedReturnBasketLineIds);
                            commit('setLastReturns', response.data);
                            commit('setLastOptions', options);

                            resolve(response);
                        })
                        .catch(error =>
                        {
                            console.log(error);
                            reject(error);
                        })
                        .finally(() =>
                        {
                            commit('overlay/setVisibility', false, { root: true });
                        });
                });
            },
        },
    };
};
